import { type ComponentType } from 'react';

import { Navigate, type RouteObject } from 'react-router-dom';

import { MainLayout } from '@widgets/main-layout/MainLayout';

import { NotFoundPage } from './NotFoundPage';
import { RouteError } from './RouteError';

// Код страниц — отдельные чанки. Один реестр исходных модулей: по нему браузер грузит страницы
// (и preloadPages докачивает их в фоне), а пререндер находит чанк страницы в Vite manifest
const PAGE_MODULES = {
  homePage: 'src/pages/home/HomePage.tsx',
  smartPlaygroundPage: 'src/pages/smart-playground/SmartPlaygroundPage.tsx',
  sportPlatformsPage: 'src/pages/sport-platforms/SportPlatformsPage.tsx',
  trainersPage: 'src/pages/trainers/TrainersPage.tsx',
  projectsPage: 'src/pages/projects/ProjectsPage.tsx',
  contactsPage: 'src/pages/contacts/ContactsPage.tsx',
  privacyPage: 'src/pages/privacy/PrivacyPage.tsx',
  consentPage: 'src/pages/consent/ConsentPage.tsx',
  cookiesPage: 'src/pages/cookies/CookiesPage.tsx',
} as const;

type PageName = keyof typeof PAGE_MODULES;
type PageLoader = () => Promise<{ default: ComponentType }>;

const moduleLoaders = import.meta.glob<{ default: ComponentType }>('/src/pages/**/*Page.tsx');

export const PAGE_LOADERS = Object.fromEntries(
  Object.entries(PAGE_MODULES).map(([name, source]) => {
    const load = moduleLoaders[`/${source}`];
    if (!load) throw new Error(`Не найден модуль страницы: ${source}`);
    return [name, load];
  }),
) as Record<PageName, PageLoader>; // as: Object.fromEntries теряет связь ключей и значений

// Генератору HTML нужны готовые компоненты — загружаем все страницы сразу
const SSR_PAGES = import.meta.env.SSR
  ? (Object.fromEntries(
      await Promise.all(
        Object.entries(PAGE_LOADERS).map(async ([name, load]) => [name, (await load()).default]),
      ),
    ) as Record<PageName, ComponentType>) // as: Object.fromEntries теряет связь ключей и значений
  : undefined;

/**
 * Страница маршрута. В браузере — route.lazy: роутер ждёт чанк до смены экрана, старая страница
 * остаётся на месте вместо «Загрузка…», а начальный маршрут main.tsx дожидается до гидратации
 */
const page = (name: PageName) => ({
  // Пререндер по совпавшему маршруту находит чанк страницы для modulepreload
  handle: { pageModule: PAGE_MODULES[name] },
  ...(SSR_PAGES
    ? { Component: SSR_PAGES[name] }
    : { lazy: async () => ({ Component: (await PAGE_LOADERS[name]()).default }) }),
});

export const routes: RouteObject[] = [
  {
    path: '/',
    element: <MainLayout />,
    errorElement: <RouteError />,
    children: [
      {
        index: true,
        ...page('homePage'),
      },
      {
        path: 'catalog',
        children: [
          {
            index: true,
            element: <Navigate to="/" replace />, // Каталог — дропдаун, не страница
          },
          {
            path: 'smart-playground',
            children: [
              { index: true, ...page('smartPlaygroundPage') },
              { path: ':id', ...page('smartPlaygroundPage') },
            ],
          },
          {
            path: 'sport-platforms',
            children: [
              { index: true, ...page('sportPlatformsPage') },
              { path: ':id', ...page('sportPlatformsPage') },
            ],
          },
          {
            path: 'trainers',
            children: [
              { index: true, ...page('trainersPage') },
              { path: ':id', ...page('trainersPage') },
            ],
          },
          {
            // Незавершённые категории и неизвестные адреса не дублируют главную.
            path: ':slug',
            element: <NotFoundPage />,
          },
        ],
      },
      {
        path: 'projects',
        ...page('projectsPage'),
      },
      {
        path: 'contacts',
        ...page('contactsPage'),
      },
      {
        // Документы по 152-ФЗ: политика публикуется открыто, согласие — отдельный документ
        path: 'privacy',
        ...page('privacyPage'),
      },
      {
        path: 'consent',
        ...page('consentPage'),
      },
      {
        // Что такое cookie и какие используются — сюда ведёт ссылка из уведомления о cookie
        path: 'cookies',
        ...page('cookiesPage'),
      },
      {
        path: '404',
        element: <NotFoundPage />,
      },
      {
        path: '*',
        element: <NotFoundPage />,
      },
    ],
  },
];
